import React, { useState } from "react";
import Modal from "../utils/Modal/Modal";
import { ReactComponent as CloseIcon } from "../../assets/icons/close.svg";
import { ReactComponent as ChevronIcon } from "../../assets/icons/chevron-down.svg";
import { ReactComponent as LockIcon } from "../../assets/icons/lock.svg";
import { ReactComponent as StarIcon } from "../../assets/icons/star.svg";

import {
  ModalContainer,
  PromptCloseButton,
  PromptInputContainer,
  PromptInputTitle,
  PromptModalButtons,
  PromptModalHeader,
  PromptModalTitle,
  PromptSeedInput,
  PromptTextarea,
  VideoQualityContainer,
  VideoQualityTitle,
  FpsContainer,
  FpsTitle,
  DurationContainer,
  DurationTitle,
  Buttons,
  Button,
  PromptRow,
  Badge,
  RelativeContainer,
  EstTimeText,
  EstTimeContainer,
  SummaryContainer,
  CancelButton,
  ConfirmButton,
  StyledRow,
  AnimatedPromptContainer,
  StyledPromptInputContainer,
  AudioContainer,
  AudioRow,
  AudioTitle,
  StyledBadge,
} from "./EnterPromptModal.styles";
import {
  ALL_VIDEO_RESOLUTIONS,
  DEFAULT_VIDEO_DURATION,
  DEFAULT_VIDEO_FPS,
  PRO_VIDEO_DURATION,
  VIDEO_RESOLUTION,
  VIDEO_DURATION_PRICE,
  VIDEO_RESOLUTION_PRICE,
  VIDEO_FPS_PRICE,
  PRO_PLAN_NAME,
  PREMIUM_PLAN_NAME,
  BASIC_PLAN_NAME,
  AUDIO_PRICE,
} from "../../utils/constants";
import { useDispatch, useSelector } from "react-redux";
import { selectUser } from "../../store/slices/user/slice";
import { setVideoSettings } from "../../store/slices/common/slice";
import UnlockModal from "../UnlockModal/UnlockModal";
import { ReactComponent as BetaIcon } from "../../assets/icons/beta.svg";
import LottieIcon from "../LottieIcon/LottieIcon";
import checkboxAnimation from "../../assets/animations/radioButton.json";

const VIDEO_RESOLUTION_DESCRIPTION = {
  LOW: "2 mins",
  MEDIUM: "3-5 mins",
  HIGH: "4-10 mins",
};

const FPS_OPTIONS = [30, 45, 60];
const DURATION_OPTIONS = [5, 8, 10, 12];

const EnterPromptModal = ({
  isOpen,
  onClose,
  customPrompt,
  setCustomPrompt,
  negativePrompt,
  setNegativePrompt,
  audioEnabled,
  setAudioEnabled,
  videoResolution,
  setVideoResolution,
  enableVideoQuality,
  customSeed,
  setCustomSeed,
  fps,
  collectionSeed,
  showPrompts,
  setShowPrompts,
  setFps,
  videoDuration,
  setVideoDuration,
  modelPrice,
  resetSeed,
  isMinVideoLength8sec,
  collectionId,
  overlayZIndex = 3500,
  hideAudio = false,
}) => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const [openUnlockModal, setOpenUnlockModal] = useState(false);
  const [seedMode, setSeedMode] = React.useState(() => {
    return localStorage.getItem("seedMode") || "random";
  });

  const isProEnabled =
    [PRO_PLAN_NAME, PREMIUM_PLAN_NAME].includes(user?.subscription?.name) &&
    user?.subscription?.endDate &&
    new Date(user?.subscription?.endDate) > new Date();

  const isBasicEnabled =
    user?.subscription?.name === BASIC_PLAN_NAME &&
    user?.subscription?.endDate &&
    new Date(user?.subscription?.endDate) > new Date();

  const isSubscribed = isBasicEnabled || isProEnabled;

  const calculateTotalPrice = (modelPrice) => {
    let totalPrice = modelPrice;

    if (videoResolution && VIDEO_RESOLUTION_PRICE[videoResolution]) {
      totalPrice += VIDEO_RESOLUTION_PRICE[videoResolution];
    }

    if (videoDuration && VIDEO_DURATION_PRICE[videoDuration]) {
      totalPrice += VIDEO_DURATION_PRICE[videoDuration];
    }

    if (fps && VIDEO_FPS_PRICE[fps]) {
      totalPrice += VIDEO_FPS_PRICE[fps];
    }

    if (audioEnabled) {
      totalPrice += AUDIO_PRICE;
    }

    return totalPrice;
  };

  const totalPrice = calculateTotalPrice(modelPrice);

  React.useEffect(() => {
    if (!isOpen) return;

    dispatch(
      setVideoSettings({
        audioEnabled,
        videoResolution,
        fps,
        videoDuration,
      }),
    );
  }, [isOpen, audioEnabled, videoResolution, fps, videoDuration, dispatch]);

  const generateRandomSeed = () => {
    return Math.floor(Math.random() * 10000000) + 1;
  };

  React.useEffect(() => {
    if (!isOpen) return;

    const savedSeedMode = localStorage.getItem("seedMode");
    if (savedSeedMode) setSeedMode(savedSeedMode);

    const savedResolution = localStorage.getItem("videoResolution");
    if (savedResolution) setVideoResolution(savedResolution);

    const savedFps = Number(localStorage.getItem("videoFps"));
    if (savedFps) setFps(savedFps);

    const savedDuration = Number(localStorage.getItem("videoDuration"));
    if (savedDuration) setVideoDuration(savedDuration);

    setAudioEnabled(localStorage.getItem("audioEnabled") === "true");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  React.useEffect(() => {
    if (seedMode === "random") {
      setCustomSeed(generateRandomSeed());
      return;
    }

    if (seedMode === "default") {
      setCustomSeed(collectionSeed ?? generateRandomSeed());
      return;
    }

    if (seedMode === "custom") {
      const savedCustomSeed = localStorage.getItem("customSeed");
      if (savedCustomSeed) {
        setCustomSeed(Number(savedCustomSeed));
      } else {
        setCustomSeed(collectionSeed ?? generateRandomSeed());
      }
    }
  }, [seedMode, setCustomSeed, collectionSeed, resetSeed]);

  React.useEffect(() => {
    if (!Number(localStorage.getItem("videoDuration")) && collectionId) {
      const defaultDuration = isMinVideoLength8sec ? 8 : 5;
      setVideoDuration(defaultDuration);
      localStorage.setItem("videoDuration", defaultDuration);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMinVideoLength8sec, setVideoDuration, collectionId]);

  React.useEffect(() => {
    if (user) {
      if (!isSubscribed) {
        setFps(30);
        localStorage.setItem("videoFps", 30);

        if (!isMinVideoLength8sec) {
          setVideoDuration(DEFAULT_VIDEO_DURATION);
          localStorage.setItem("videoDuration", DEFAULT_VIDEO_DURATION);
        } else {
          if (PRO_VIDEO_DURATION.includes(videoDuration)) {
            setVideoDuration(8);
            localStorage.setItem("videoDuration", 8);
          }
        }

        setVideoResolution(VIDEO_RESOLUTION.LOW);
        localStorage.setItem("videoResolution", VIDEO_RESOLUTION.LOW);
      } else {
        const maxAllowed = isProEnabled ? 60 : 45;
        const allowedFps = Math.min(fps || DEFAULT_VIDEO_FPS, maxAllowed);
        setFps(allowedFps);

        localStorage.setItem("videoFps", allowedFps);
        if (!isProEnabled) {
          if (videoResolution === VIDEO_RESOLUTION.HIGH) {
            setVideoResolution(VIDEO_RESOLUTION.MEDIUM);
            localStorage.setItem("videoResolution", VIDEO_RESOLUTION.MEDIUM);
          }
          if (PRO_VIDEO_DURATION.includes(videoDuration)) {
            setVideoDuration(isMinVideoLength8sec ? 8 : DEFAULT_VIDEO_DURATION);
            localStorage.setItem(
              "videoDuration",
              isMinVideoLength8sec ? 8 : DEFAULT_VIDEO_DURATION,
            );
          }
        }
      }
    }
  }, [
    fps,
    isSubscribed,
    isProEnabled,
    setVideoResolution,
    setVideoDuration,
    videoResolution,
    videoDuration,
    setFps,
    user,
    isMinVideoLength8sec,
  ]);

  const isDurationEnabled = (duration) => {
    if (!isSubscribed)
      return duration === 5 || (isMinVideoLength8sec && duration === 8);
    if (isBasicEnabled) return duration <= 8;
    if (isProEnabled) return true;
    return false;
  };

  const shouldShowDurationBadge = (duration) => {
    if (duration === 5) return null;
    if (duration === 8) {
      return !isSubscribed && !isMinVideoLength8sec ? "basic" : null;
    }
    if (duration === 10 || duration === 12) {
      return !isProEnabled ? "pro" : null;
    }
    return null;
  };

  const isFpsEnabled = (fpsValue) => {
    if (fpsValue === 30) return true;
    if (fpsValue === 45) return isBasicEnabled || isProEnabled;
    if (fpsValue === 60 || fpsValue === 120) return isProEnabled;
    return false;
  };

  const shouldShowFpsBadge = (fpsValue) => {
    if (fpsValue === 30) return null;
    if (fpsValue === 45) {
      return !isSubscribed ? "basic" : null;
    }
    if (fpsValue === 60 || fpsValue === 120) {
      return !isProEnabled ? "pro" : null;
    }
    return null;
  };

  const handleFpsClick = (fpsValue) => {
    if (isFpsEnabled(fpsValue)) {
      setFps(fpsValue);
      localStorage.setItem("videoFps", fpsValue);
    }
  };

  const handleDurationClick = (duration) => {
    if (isDurationEnabled(duration) && videoDuration !== duration) {
      setVideoDuration(duration);
      localStorage.setItem("videoDuration", duration);
    }
  };

  const handleAudioToggle = () => {
    const newValue = !audioEnabled;
    setAudioEnabled(newValue);
    localStorage.setItem("audioEnabled", String(newValue));
  };

  const shouldShowVideoQualityBadge = (quality) => {
    if (quality === "LOW") return null;
    if (quality === "MEDIUM") {
      return !isSubscribed ? "basic" : null;
    }
    if (quality === "HIGH") {
      return !isProEnabled ? "pro" : null;
    }
    return null;
  };

  const isVideoQualityEnabled = (quality) => {
    if (quality === "LOW") return true;
    if (quality === "MEDIUM") return isBasicEnabled || isProEnabled;
    if (quality === "HIGH") return isProEnabled;
    return false;
  };

  const renderDurationOption = (duration) => {
    const isEnabled = isDurationEnabled(duration);
    const isSelected = videoDuration === duration;
    const badge = shouldShowDurationBadge(duration);

    return (
      <Button
        key={duration}
        $disabled={!isEnabled}
        $active={isSelected}
        onClick={() => {
          if (isEnabled) {
            handleDurationClick(duration);
          } else {
            setOpenUnlockModal(true);
          }
        }}
      >
        {duration}s
        {badge === "basic" && (
          <Badge>
            <LockIcon />
            Basic
          </Badge>
        )}
        {badge === "pro" && (
          <Badge $pro>
            <LockIcon />
            Pro
          </Badge>
        )}
      </Button>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      customOverlayStyles={{ zIndex: overlayZIndex }}
    >
      <ModalContainer>
        <PromptModalHeader>
          <PromptModalTitle>Customize Scene</PromptModalTitle>
          <PromptCloseButton
            onClick={() => {
              onClose();
            }}
          >
            <CloseIcon />
          </PromptCloseButton>
        </PromptModalHeader>

        <PromptInputContainer>
          <PromptInputTitle>Seed</PromptInputTitle>
          <PromptRow>
            <Buttons>
              <Button
                $active={seedMode === "random"}
                onClick={() => {
                  setSeedMode("random");
                  localStorage.setItem("seedMode", "random");
                  setCustomSeed(generateRandomSeed());
                }}
              >
                Random
              </Button>
              <Button
                $active={seedMode === "default"}
                onClick={() => {
                  setSeedMode("default");
                  localStorage.setItem("seedMode", "default");
                  setCustomSeed(collectionSeed);
                }}
              >
                Default
              </Button>
              <Button
                $active={seedMode === "custom"}
                onClick={() => {
                  setSeedMode("custom");
                  localStorage.setItem("seedMode", "custom");
                }}
              >
                Custom
              </Button>
            </Buttons>
          </PromptRow>
          {seedMode === "custom" && (
            <PromptSeedInput
              disabled={seedMode === "random"}
              placeholder="Enter your seed here..."
              value={customSeed}
              min={1}
              max={9007199254740991}
              onChange={(e) => {
                const value = Number(e.target.value);
                if (value >= 0 && value <= 9007199254740991) {
                  setCustomSeed(value > 0 ? value : 1);
                  if (seedMode === "custom") {
                    localStorage.setItem("customSeed", value > 0 ? value : 1);
                  }
                }
              }}
            />
          )}
        </PromptInputContainer>

        <VideoQualityContainer>
          {enableVideoQuality && (
            <>
              <VideoQualityTitle>Video Quality</VideoQualityTitle>
              <Buttons>
                {ALL_VIDEO_RESOLUTIONS.map((videoResolutionType) => {
                  const badge =
                    shouldShowVideoQualityBadge(videoResolutionType);
                  const isEnabled = isVideoQualityEnabled(videoResolutionType);

                  return (
                    <Button
                      onClick={() => {
                        if (isEnabled) {
                          setVideoResolution(videoResolutionType);
                          localStorage.setItem(
                            "videoResolution",
                            videoResolutionType,
                          );
                        } else {
                          setOpenUnlockModal(true);
                        }
                      }}
                      $active={videoResolutionType === videoResolution}
                      key={videoResolutionType}
                      $disabled={!isEnabled}
                    >
                      {videoResolutionType.charAt(0).toUpperCase() +
                        videoResolutionType.slice(1).toLowerCase()}

                      {badge === "basic" && (
                        <Badge>
                          <LockIcon />
                          Basic
                        </Badge>
                      )}
                      {badge === "pro" && (
                        <Badge $pro>
                          <LockIcon />
                          Pro
                        </Badge>
                      )}
                    </Button>
                  );
                })}
              </Buttons>
            </>
          )}
        </VideoQualityContainer>

        {enableVideoQuality && (
          <FpsContainer>
            <FpsTitle>FPS</FpsTitle>

            <Buttons>
              {FPS_OPTIONS.map((fpsValue) => {
                const badge = shouldShowFpsBadge(fpsValue);
                const isEnabled = isFpsEnabled(fpsValue);

                return (
                  <Button
                    key={fpsValue}
                    $disabled={!isEnabled}
                    $active={fps === fpsValue}
                    onClick={() => {
                      if (isEnabled) {
                        handleFpsClick(fpsValue);
                      } else {
                        setOpenUnlockModal(true);
                      }
                    }}
                  >
                    {fpsValue} FPS
                    {badge === "basic" && (
                      <Badge>
                        <LockIcon />
                        Basic
                      </Badge>
                    )}
                    {badge === "pro" && (
                      <Badge $pro>
                        <LockIcon />
                        Pro
                      </Badge>
                    )}
                  </Button>
                );
              })}
            </Buttons>
          </FpsContainer>
        )}

        {enableVideoQuality && (
          <DurationContainer>
            <DurationTitle>Duration</DurationTitle>
            <Buttons>{DURATION_OPTIONS.map(renderDurationOption)}</Buttons>
          </DurationContainer>
        )}

        <StyledPromptInputContainer $gap={showPrompts}>
          <StyledRow
            $expanded={showPrompts}
            onClick={() => {
              setShowPrompts(!showPrompts);
            }}
          >
            <PromptInputTitle style={{ fontSize: 15 }}>
              Edit Prompt
            </PromptInputTitle>
            <ChevronIcon />
          </StyledRow>
          <AnimatedPromptContainer $expanded={showPrompts}>
            <PromptInputContainer>
              <PromptInputTitle>Custom Prompt</PromptInputTitle>
              <RelativeContainer
                onClick={() => {
                  //   if (!isSubscribed) {
                  //     setOpenUnlockModal(true);
                  //   }
                }}
              >
                <PromptTextarea
                  $minHeight
                  placeholder="Enter your custom prompt here..."
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                />
                {/* {!isSubscribed && (
                <Badge>
                  <LockIcon />
                  Basic
                </Badge>
              )} */}
              </RelativeContainer>
            </PromptInputContainer>
            <PromptInputContainer>
              <PromptInputTitle>Negative Prompt</PromptInputTitle>
              <RelativeContainer>
                <PromptTextarea
                  placeholder="Enter your negative prompt here..."
                  value={negativePrompt}
                  onChange={(e) => setNegativePrompt(e.target.value)}
                />
              </RelativeContainer>
            </PromptInputContainer>
          </AnimatedPromptContainer>
        </StyledPromptInputContainer>

        {enableVideoQuality && !hideAudio && (
          <AudioContainer>
            <AudioRow onClick={handleAudioToggle}>
              <LottieIcon
                animationData={checkboxAnimation}
                trigger="toggle-reverse"
                isActive={audioEnabled}
                speed={2}
                onActiveChange={handleAudioToggle}
                onClick={handleAudioToggle}
                size={24}
                strokeColor={"#2877d9"}
              />
              <AudioTitle>
                Audio
                <StyledBadge>
                  <BetaIcon />
                </StyledBadge>
              </AudioTitle>
            </AudioRow>
          </AudioContainer>
        )}

        <SummaryContainer>
          <EstTimeContainer>
            <EstTimeText>Est Time:</EstTimeText>
            <EstTimeText $bold>
              {VIDEO_RESOLUTION_DESCRIPTION[videoResolution]}
            </EstTimeText>
          </EstTimeContainer>
          <EstTimeContainer>
            <EstTimeText>Cost:</EstTimeText>
            <StarIcon />
            <EstTimeText $bold>{totalPrice}</EstTimeText>
          </EstTimeContainer>
        </SummaryContainer>

        <PromptModalButtons>
          <ConfirmButton
            $type="primary"
            onClick={() => {
              onClose(false);
            }}
            disabled={false}
          >
            Save
          </ConfirmButton>

          <CancelButton
            onClick={() => {
              onClose();
            }}
          >
            Cancel
          </CancelButton>
        </PromptModalButtons>
      </ModalContainer>
      <UnlockModal
        isOpen={openUnlockModal}
        onClose={() => setOpenUnlockModal(false)}
      />
    </Modal>
  );
};

export default EnterPromptModal;
